// src/components/settings/know-your-numbers-tab.jsx
// Know Your Numbers — fills the IMPACT coaching worksheet from live data.
// Actuals auto-calculate (Me or My Team) and stay editable; personal answers
// save per-user and carry over; every derived number (ratio, wage, gaps) comes
// from deriveKnowYourNumbers so the screen and the PDF always agree.
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Download, Loader2, Lock, Save } from "lucide-react";
import { toast } from "react-toastify";
import moment from "moment";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { isImpactScorecardEnabled } from "../../utils/featureFlags";
import {
  getAlldealforAdmin,
  listenBuyerLeads,
  listenListingLeads,
} from "../../utils/firebasefunctions";
import {
  computeKnowYourNumbersMetrics,
  deriveKnowYourNumbers,
  buildKnowYourNumbersValues,
  parseNum,
  fmtMoney,
  kynBasisNote,
  kynFieldLabel,
  incomeSplitSentence,
} from "../../utils/knowYourNumbers";
import {
  loadKnowYourNumbers,
  saveKnowYourNumbers,
  KYN_FALLBACK,
} from "../../utils/knowYourNumbersStore";
import {
  fillKnowYourNumbersPdf,
  downloadBytes,
} from "../../utils/knowYourNumbersPdf";

const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-themeColor";
const labelCls = "text-[13px] text-slate-500 mb-1 block";
const fmtPct = (n) => (n == null ? "" : `${n.toFixed(2)}%`);

// The one-line "whose deals is this" note that sits under a number. Keeping it
// its own component means every number on the sheet is labelled the same way.
function Basis({ children }) {
  if (!children) return null;
  return (
    <span className="text-[11px] leading-snug text-slate-400 mt-1 block">
      {children}
    </span>
  );
}

// A computed, read-only result (the sheet's "=" cells).
function Derived({ label, value, basis }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <span className="text-[12px] text-slate-500 block">{label}</span>
      <span className="text-sm font-semibold text-linkColor">
        {value === "" ? "-" : value}
      </span>
      <Basis>{basis}</Basis>
    </div>
  );
}

function Section({ n, title, note, children }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
      <p className="font-semibold text-slate-800">
        {n} · {title}{" "}
        {note && (
          <span className="font-normal text-[12px] text-slate-400">({note})</span>
        )}
      </p>
      {children}
    </div>
  );
}

export default function KnowYourNumbersTab() {
  const [{ user, deals, buyerLeads, listingLeads }, dispatch] = useStateValue();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaved, setAutoSaved] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [scope, setScope] = useState("me");
  const [typed, setTyped] = useState({ ...KYN_FALLBACK });
  // What's already in Firestore, so auto-save only writes real changes.
  const lastSavedRef = useRef("");
  // Latest unsaved payload, so leaving the tab saves immediately.
  const pendingRef = useRef(null);

  // Typed-over auto numbers live in the saved doc, per scope, so a corrected
  // number stays corrected across visits. Empty = back to auto.
  const overridesKey = scope === "me" ? "overridesMe" : "overridesTeam";
  const overrides = typed[overridesKey] || {};

  const metrics = useMemo(() => {
    const data = { deals, buyerLeads, listingLeads };
    const asOf = new Date();
    return {
      me: computeKnowYourNumbersMetrics(data, { agentId: user?.id, asOf }),
      team: computeKnowYourNumbersMetrics(data, { asOf }),
    };
  }, [deals, buyerLeads, listingLeads, user?.id]);

  const m = scope === "me" ? metrics.me : metrics.team;
  const numbers = useMemo(
    () => ({
      unitsYtd: overrides.unitsYtd ?? String(m.unitsYtd),
      volumeYtd: overrides.volumeYtd ?? fmtMoney(m.volumeYtd),
      incomeYtd: overrides.incomeYtd ?? fmtMoney(m.incomeYtd),
      avgSalesPrice: overrides.avgSalesPrice ?? fmtMoney(m.avgSalesPrice),
      avgCommissionPct: overrides.avgCommissionPct ?? fmtPct(m.avgCommissionPct),
      leadsPerMonth: overrides.leadsPerMonth ?? String(m.leadsPerMonth),
      closingsPerMonth: overrides.closingsPerMonth ?? String(m.closingsPerMonth),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [m, typed[overridesKey]],
  );

  const derived = useMemo(
    () =>
      deriveKnowYourNumbers({
        incomeYtd: parseNum(numbers.incomeYtd),
        unitsYtd: parseNum(numbers.unitsYtd),
        volumeYtd: parseNum(numbers.volumeYtd),
        avgSalesPrice: parseNum(numbers.avgSalesPrice),
        avgCommissionPct: parseNum(numbers.avgCommissionPct),
        perFileCosts: parseNum(typed.perFileCosts),
        leadsPerMonth: parseNum(numbers.leadsPerMonth),
        closingsPerMonth: parseNum(numbers.closingsPerMonth),
        hoursPerWeek: parseNum(typed.hoursPerWeek),
        weeksWorkedYtd: parseNum(typed.weeksWorkedYtd),
        goalDate: typed.goalDate,
        seatsToday: parseNum(typed.seatsToday),
        goals: {
          income: parseNum(typed.goals.income),
          volume: parseNum(typed.goals.volume),
          closings: parseNum(typed.goals.closings),
          leadsPerMonth: parseNum(typed.goals.leadsPerMonth),
          hoursPerWeek: parseNum(typed.goals.hoursPerWeek),
          teamSeats: parseNum(typed.goals.teamSeats),
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [numbers, typed],
  );

  // Fresh data on open (Lead Tracker pattern): the user may land in Settings
  // before the dashboard's mount fetches have filled the store.
  useEffect(() => {
    getAlldealforAdmin("deals")
      .then((rows) => {
        dispatch({ type: actionType.STORE_DEALS, deals: rows });
      })
      .catch(() => {});
    const unsubListing = listenListingLeads((rows) =>
      dispatch({ type: actionType.STORE_LISTING_LEADS, payload: rows }),
    );
    const unsubBuyer = listenBuyerLeads((rows) =>
      dispatch({ type: actionType.STORE_BUYER_LEADS, payload: rows }),
    );
    return () => {
      if (typeof unsubListing === "function") unsubListing();
      if (typeof unsubBuyer === "function") unsubBuyer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    loadKnowYourNumbers(user.id).then((s) => {
      if (cancelled) return;
      setTyped(s);
      const scope0 = s.lastScope === "team" ? "team" : "me";
      setScope(scope0);
      lastSavedRef.current = JSON.stringify({ ...s, lastScope: scope0 });
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Auto-save: any change to the answers (typed, overrides, or scope) lands in
  // Firestore ~1s after the user stops typing, so nothing is lost without
  // pressing Save.
  useEffect(() => {
    if (loading || !user?.id) return undefined;
    const payload = { ...typed, lastScope: scope };
    const json = JSON.stringify(payload);
    if (json === lastSavedRef.current) {
      pendingRef.current = null;
      return undefined;
    }
    setAutoSaved(false);
    pendingRef.current = { uid: user.id, payload, json };
    const timer = setTimeout(() => {
      saveKnowYourNumbers(user.id, payload)
        .then(() => {
          lastSavedRef.current = json;
          pendingRef.current = null;
          setAutoSaved(true);
        })
        .catch(() =>
          toast.error("Couldn't auto-save. Use Save My Answers to try again.", {
            toastId: "kyn-autosave-fail",
          }),
        );
    }, 1000);
    return () => clearTimeout(timer);
  }, [typed, scope, loading, user?.id]);

  // Leaving the tab before the 1s timer fires still saves the last edit.
  useEffect(
    () => () => {
      const pending = pendingRef.current;
      if (pending) saveKnowYourNumbers(pending.uid, pending.payload).catch(() => {});
    },
    [],
  );

  const setField = (key, value) => setTyped((t) => ({ ...t, [key]: value }));
  const setGoal = (key, value) =>
    setTyped((t) => ({ ...t, goals: { ...t.goals, [key]: value } }));
  const setRow = (key, idx, patch) =>
    setTyped((t) => {
      const next = t[key].map((row, i) => (i === idx ? { ...row, ...patch } : row));
      return { ...t, [key]: next };
    });
  const setGapText = (idx, value) =>
    setTyped((t) => {
      const next = [...t.biggestGaps];
      next[idx] = value;
      return { ...t, biggestGaps: next };
    });
  // Typing replaces the auto number (and persists); clearing the field
  // removes the override so the live auto value comes back.
  const setNum = (key, value) =>
    setTyped((t) => {
      const next = { ...(t[overridesKey] || {}) };
      if (value === "") delete next[key];
      else next[key] = value;
      return { ...t, [overridesKey]: next };
    });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...typed, lastScope: scope };
      await saveKnowYourNumbers(user.id, payload);
      lastSavedRef.current = JSON.stringify(payload);
      setAutoSaved(true);
      toast.success("Know Your Numbers answers saved");
    } catch (err) {
      toast.error("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const name =
        [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
        user?.name ||
        user?.email ||
        "";
      const values = buildKnowYourNumbersValues({
        name,
        date: moment().format("MM/DD/YYYY"),
        numbers,
        typed,
        derived,
        scope,
      });
      const bytes = await fillKnowYourNumbersPdf(values);
      downloadBytes(bytes, `Know Your Numbers - ${moment().format("MMMM YYYY")}.pdf`);
      toast.success("Know Your Numbers PDF generated");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't generate the PDF. Check the template.");
    } finally {
      setGenerating(false);
    }
  };

  if (!isImpactScorecardEnabled({ user })) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  // Every number's "whose deals does this cover" note, bound to the scope the
  // user is currently generating for.
  const basis = (field) => kynBasisNote(field, scope);
  // The same answer, in the LABEL. Income YTD is every dollar he was paid;
  // everything else on these two sections is his own closings. A broker
  // comparing "1 closing" against "$11,210.20" reads the labels, not the small
  // print underneath them, and the two figures sit a section apart.
  const label = (field) => kynFieldLabel(field, scope);
  // Reads "Your $11,210.20 is $7,666.00 earned on your own 1 closing, plus
  // $3,544.20 earned on other agents' closings." Blank in the My Team view and
  // in any year where no other agent's deal paid this person. Suppressed when
  // the income line has been typed over, because it explains the AUTO number
  // and would otherwise describe a figure that is no longer on screen.
  const incomeSplit = overrides.incomeYtd == null ? incomeSplitSentence(m) : "";

  const gapRows = [
    { key: "income", label: "Income", today: numbers.incomeYtd, goalKey: "income", gap: fmtMoney(derived.gaps.income) },
    { key: "volume", label: "Volume", today: numbers.volumeYtd, goalKey: "volume", gap: fmtMoney(derived.gaps.volume) },
    { key: "closings", label: "Closings (units)", today: numbers.unitsYtd, goalKey: "closings", gap: derived.gaps.closings == null ? "" : String(derived.gaps.closings) },
    { key: "leads", label: "Leads per month", today: numbers.leadsPerMonth, goalKey: "leadsPerMonth", gap: derived.gaps.leadsPerMonth == null ? "" : String(derived.gaps.leadsPerMonth) },
    { key: "hours", label: "Hours per week", today: typed.hoursPerWeek, goalKey: "hoursPerWeek", gap: derived.gaps.hoursPerWeek == null ? "" : String(derived.gaps.hoursPerWeek) },
    { key: "seats", label: "Team seats filled", today: typed.seatsToday, goalKey: "teamSeats", gap: derived.gaps.teamSeats == null ? "" : String(derived.gaps.teamSeats) },
  ];

  return (
    <div className="max-w-3xl space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
          Know Your Numbers
        </h2>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 text-slate-500 text-[12px] px-2.5 py-1">
          <Lock className="w-3.5 h-3.5" /> Super Admin, only you
        </span>
      </div>

      <div>
        <label className={labelCls}>Production Scope</label>
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          {["me", "team"].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setScope(opt)}
              className={`px-4 py-2 text-sm ${
                scope === opt
                  ? "bg-slate-50 text-linkColor"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {opt === "me" ? "Me" : "My Team"}
            </button>
          ))}
        </div>
      </div>

      <Section n={1} title="My YTD Actuals" note="auto from your closed deals; type to correct, clear to go back to auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>{label("unitsYtd")}</label>
            <input className={inputCls} value={numbers.unitsYtd} onChange={(e) => setNum("unitsYtd", e.target.value)} />
            <Basis>{basis("unitsYtd")}</Basis>
          </div>
          <div>
            <label className={labelCls}>{label("volumeYtd")}</label>
            <input className={inputCls} value={numbers.volumeYtd} onChange={(e) => setNum("volumeYtd", e.target.value)} />
            <Basis>{basis("volumeYtd")}</Basis>
          </div>
          <div>
            <label className={labelCls}>{label("incomeYtd")}</label>
            <input className={inputCls} value={numbers.incomeYtd} onChange={(e) => setNum("incomeYtd", e.target.value)} />
            <Basis>{basis("incomeYtd")}</Basis>
          </div>
        </div>
        {incomeSplit && (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[12px] leading-snug text-slate-600">
            <strong className="font-semibold text-themeColor">
              Why Income YTD is bigger than your closings:
            </strong>{" "}
            {incomeSplit}
          </p>
        )}
      </Section>

      <Section n={2} title="My Average Income Per Closing" note="same math as the business plan">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>{label("avgSalesPrice")}</label>
            <input className={inputCls} value={numbers.avgSalesPrice} onChange={(e) => setNum("avgSalesPrice", e.target.value)} />
            <Basis>{basis("avgSalesPrice")}</Basis>
          </div>
          <div>
            <label className={labelCls}>{label("avgCommissionPct")}</label>
            <input className={inputCls} value={numbers.avgCommissionPct} onChange={(e) => setNum("avgCommissionPct", e.target.value)} />
            <Basis>{basis("avgCommissionPct")}</Basis>
          </div>
          <div>
            <label className={labelCls}>Per File Costs (90-day expenses ÷ 90-day units)</label>
            <input className={inputCls} placeholder="$0" value={typed.perFileCosts} onChange={(e) => setField("perFileCosts", e.target.value)} />
          </div>
          <Derived
            label={label("avgIncomePerClosing")}
            value={fmtMoney(derived.avgIncomePerClosing)}
            basis={basis("avgIncomePerClosing")}
          />
        </div>
      </Section>

      <Section n={3} title="My Leads & Closing Ratio" note="auto from your pipelines; type to correct, clear to go back to auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Current Leads Per Month</label>
            <input className={inputCls} value={numbers.leadsPerMonth} onChange={(e) => setNum("leadsPerMonth", e.target.value)} />
            <Basis>{basis("leadsPerMonth")}</Basis>
          </div>
          <div>
            <label className={labelCls}>Current Closings Per Month</label>
            <input className={inputCls} value={numbers.closingsPerMonth} onChange={(e) => setNum("closingsPerMonth", e.target.value)} />
            <Basis>{basis("closingsPerMonth")}</Basis>
          </div>
          <Derived label="My Closing Ratio" value={derived.closingRatio == null ? "" : derived.closingRatio.toFixed(2)} />
        </div>
      </Section>

      <Section n={4} title="My Hourly Wage" note="what your time is actually worth right now">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Avg Hours Per Week</label>
            <input className={inputCls} value={typed.hoursPerWeek} onChange={(e) => setField("hoursPerWeek", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Weeks Worked YTD</label>
            <input className={inputCls} value={typed.weeksWorkedYtd} onChange={(e) => setField("weeksWorkedYtd", e.target.value)} />
          </div>
          <Derived label="Total Hours YTD" value={derived.totalHoursYtd == null ? "" : derived.totalHoursYtd.toLocaleString("en-US")} />
          <Derived
            label="My Hourly Wage"
            value={fmtMoney(derived.hourlyWage)}
            basis={basis("hourlyWage")}
          />
        </div>
      </Section>

      <Section n={5} title="Team & Staffing Adjustments" note="tasks below your hourly wage">
        <div className="space-y-2">
          {typed.tasksBelowWage.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_90px_180px] gap-2">
              <input className={inputCls} placeholder={`Task ${i + 1} below my hourly wage`} value={row.task} onChange={(e) => setRow("tasksBelowWage", i, { task: e.target.value })} />
              <input className={inputCls} placeholder="Hrs/wk" value={row.hrs} onChange={(e) => setRow("tasksBelowWage", i, { hrs: e.target.value })} />
              <input className={inputCls} placeholder="Who should own it" value={row.owner} onChange={(e) => setRow("tasksBelowWage", i, { owner: e.target.value })} />
            </div>
          ))}
        </div>
        <div>
          <label className={labelCls}>Closing my gap at my current hours requires:</label>
          <div className="flex gap-2 flex-wrap">
            {[
              { id: "none", label: "No change" },
              { id: "delegate", label: "Delegate to my current team" },
              { id: "hire", label: "A new hire" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setField("staffingChoice", typed.staffingChoice === opt.id ? "" : opt.id)}
                className={`px-3 py-1.5 rounded-lg border text-sm ${
                  typed.staffingChoice === opt.id
                    ? "border-themeColor bg-slate-50 text-themeColor font-semibold"
                    : "border-gray-300 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-2">
          <div>
            <label className={labelCls}>The staffing adjustment I will make</label>
            <input className={inputCls} value={typed.staffingAdjustment} onChange={(e) => setField("staffingAdjustment", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>By when</label>
            <input type="date" className={inputCls} value={typed.staffingBy} onChange={(e) => setField("staffingBy", e.target.value)} />
          </div>
        </div>
      </Section>

      <Section n={6} title="The Gap" note="you set the goals, the gaps compute themselves">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>My Goal Date</label>
            <input type="date" className={inputCls} value={typed.goalDate} onChange={(e) => setField("goalDate", e.target.value)} />
          </div>
          <Derived label="Months To My Goal Date" value={derived.monthsToGoal == null ? "" : String(derived.monthsToGoal)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Team Seats Filled Today</label>
            <input className={inputCls} value={typed.seatsToday} onChange={(e) => setField("seatsToday", e.target.value)} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[12px] text-slate-500">
                <th className="py-1.5 pr-2 font-medium">My Number</th>
                <th className="py-1.5 pr-2 font-medium">Today</th>
                <th className="py-1.5 pr-2 font-medium">My Goal</th>
                <th className="py-1.5 font-medium">The Gap</th>
              </tr>
            </thead>
            <tbody>
              {gapRows.map((row) => (
                <tr key={row.key} className="border-t border-gray-100">
                  <td className="py-1.5 pr-2 text-slate-700">{row.label}</td>
                  <td className="py-1.5 pr-2 text-slate-500">{row.today || "-"}</td>
                  <td className="py-1.5 pr-2">
                    <input
                      className="w-28 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-themeColor"
                      value={typed.goals[row.goalKey]}
                      onChange={(e) => setGoal(row.goalKey, e.target.value)}
                    />
                  </td>
                  <td className="py-1.5 font-semibold text-linkColor">{row.gap || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Derived label="Gap Number In Closings (per month)" value={derived.gapClosingsPerMonth == null ? "" : String(derived.gapClosingsPerMonth)} />
          <Derived label="Gap Number In Leads (per month)" value={derived.gapLeadsPerMonth == null ? "" : String(derived.gapLeadsPerMonth)} />
        </div>
      </Section>

      <Section n={7} title="My 3 Biggest Gaps" note="which gaps matter most and why">
        <div className="space-y-2">
          {typed.biggestGaps.map((g, i) => (
            <input key={i} className={inputCls} placeholder={`Gap ${i + 1}`} value={g} onChange={(e) => setGapText(i, e.target.value)} />
          ))}
        </div>
      </Section>

      <Section n={8} title="Close The Gap: My 3 Moves" note="one move per gap, weekly activities">
        <div className="space-y-2">
          {typed.moves.map((row, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_90px_150px] gap-2">
              <input className={inputCls} placeholder={`Move ${i + 1} (a weekly activity, not a wish)`} value={row.move} onChange={(e) => setRow("moves", i, { move: e.target.value })} />
              <input className={inputCls} placeholder="Weekly #" value={row.weeklyNum} onChange={(e) => setRow("moves", i, { weeklyNum: e.target.value })} />
              <input type="date" className={inputCls} value={row.starts} onChange={(e) => setRow("moves", i, { starts: e.target.value })} />
            </div>
          ))}
        </div>
      </Section>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-[13px] text-slate-500 inline-flex items-center gap-2">
          Generating for:{" "}
          <strong className="font-medium text-linkColor">
            {scope === "me" ? "Me" : "My Team"}
          </strong>
          {autoSaved && (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <Check className="w-3.5 h-3.5" /> Auto-saved
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save My Answers
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 rounded-lg bg-themeColor px-4 py-2 text-sm text-white hover:bg-themeColor/90 disabled:opacity-60"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Generate PDF
          </button>
        </div>
      </div>
    </div>
  );
}
